import { shuffleArray } from '@saaga/shared/utils/shuffle-array.util'
import type { ReactNode } from 'react'
import { useMemo } from 'react'

import { cn } from '@/lib/utils'

interface OnboardingToolMarqueeProps {
  toolNameList: string[]
  className?: string
}

interface MarqueeRowConfig {
  nameList: string[]
  isReversed: boolean
  speedClass: string
}

const MARQUEE_ROW_COUNT = 40
const MIN_TOOLS_FOR_DISPLAY = 3

const SPEED_CLASS_LIST = [
  'animate-onboarding-marquee-slow',
  'animate-onboarding-marquee-medium',
  'animate-onboarding-marquee-fast',
]

const buildRowConfigList = (toolNameList: string[]): MarqueeRowConfig[] =>
  Array.from({ length: MARQUEE_ROW_COUNT }, (_, i) => ({
    nameList: shuffleArray(toolNameList),
    isReversed: i % 2 === 1,
    speedClass: SPEED_CLASS_LIST[i % SPEED_CLASS_LIST.length]!,
  }))

/**
 * Decorative diagonal marquee of tool names scrolling infinitely.
 * Purely visual — no interactivity. Designed as a background layer.
 */
export const OnboardingToolMarquee: React.FC<OnboardingToolMarqueeProps> = ({
  toolNameList,
  className,
}): ReactNode => {
  const rowConfigList = useMemo(() => buildRowConfigList(toolNameList), [toolNameList])

  if (toolNameList.length < MIN_TOOLS_FOR_DISPLAY) {
    return null
  }

  return (
    <div
      aria-hidden="true"
      className={cn(
        'onboarding-tool-marquee pointer-events-none absolute inset-0 select-none overflow-hidden',
        className
      )}
    >
      <div className="absolute inset-0 flex -translate-y-1/4 -rotate-12 scale-150 flex-col justify-center gap-0">
        {rowConfigList.map((row, rowIndex) => (
          <MarqueeRow key={rowIndex} config={row} />
        ))}
      </div>
    </div>
  )
}

interface MarqueeRowProps {
  config: MarqueeRowConfig
}

const MarqueeRow: React.FC<MarqueeRowProps> = ({ config }): ReactNode => {
  const { nameList, isReversed, speedClass } = config
  const joined = nameList.join(' · ')
  const content = `${joined} · `

  return (
    <div className="flex w-full shrink-0 overflow-hidden whitespace-nowrap">
      <div
        className={cn(
          'flex shrink-0',
          isReversed ? 'animate-onboarding-marquee-reverse' : 'animate-onboarding-marquee-forward',
          speedClass
        )}
      >
        <span className="marquee-text shrink-0 text-3xl font-semibold text-foreground/[0.10] dark:text-foreground/[0.12]">
          {content}
        </span>
        <span className="marquee-text shrink-0 text-3xl font-semibold text-foreground/[0.10] dark:text-foreground/[0.12]">
          {content}
        </span>
      </div>
    </div>
  )
}
